import React from 'react';
import { motion } from 'framer-motion';
import { 
  UserPlus, 
  UploadCloud, 
  Send, 
  ArrowDown, 
  Sparkles, 
  Zap, 
  MessageSquare, 
  Mail, 
  Smartphone,
  MessageCircle
} from 'lucide-react';

// --- 1. Background Elements (Kept Same) ---
const BackgroundElements = () => {
  const icons = [
    { Icon: MessageSquare, color: "#0C4550", size: 40, x: "5%", y: "10%", delay: 0 },
    { Icon: Mail, color: "#1a6b7a", size: 30, x: "85%", y: "15%", delay: 1 },
    { Icon: Smartphone, color: "#64748b", size: 35, x: "15%", y: "60%", delay: 2 },
    { Icon: MessageCircle, color: "#F46653", size: 45, x: "80%", y: "75%", delay: 0.5 },
    { Icon: Send, color: "#1a6b7a", size: 50, x: "5%", y: "35%", delay: 1.5 },
    { Icon: MessageSquare, color: "#eab308", size: 28, x: "90%", y: "45%", delay: 2.5 },
    { Icon: Mail, color: "#0C4550", size: 24, x: "55%", y: "5%", delay: 3 },
    { Icon: MessageCircle, color: "#cbd5e1", size: 40, x: "40%", y: "85%", delay: 1 },
  ];

  return (
    <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none select-none">
      <div className="absolute inset-0 bg-gradient-to-b from-gray-50 via-white to-blue-50/20" />
      <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:32px_32px] opacity-60"></div>
      
      <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-purple-100/40 rounded-full blur-[100px] mix-blend-multiply" />
      <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-orange-100/40 rounded-full blur-[100px] mix-blend-multiply" />

      {icons.map((item, index) => (
        <motion.div
          key={index}
          className="absolute"
          style={{ left: item.x, top: item.y }}
          initial={{ opacity: 0, y: 0 }}
          whileInView={{ opacity: 0.2 }}
          animate={{ y: [0, -15, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: item.delay }}
        >
          <item.Icon size={item.size} color={item.color} strokeWidth={1.5} />
        </motion.div>
      ))}
    </div>
  );
};

// --- 2. Floating Icon (Kept Same) ---
const FloatingIcon = ({ icon: Icon }) => {
  return (
    <div className="relative group/icon cursor-pointer inline-block">
      <motion.div 
        className="w-20 h-20 bg-gradient-to-br from-white to-gray-50 rounded-full border border-gray-200 shadow-xl flex items-center justify-center relative z-10"
        whileHover={{ scale: 1.05 }}
        transition={{ duration: 0.2 }}
      >
        <Icon size={32} className="text-[#0C4550] group-hover/card:text-[#F46653] transition-colors duration-300" strokeWidth={1.5} />
      </motion.div>
      <div className="absolute inset-0 rounded-full border border-dashed border-[#0C4550]/20 scale-125 pointer-events-none" />
    </div>
  );
};

// --- 3. Puzzle Pattern (Kept Same) ---
const PuzzlePattern = () => {
  return (
    <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden opacity-40">
      <svg className="absolute w-full h-full" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <pattern id="puzzle-grid" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
             <path d="M40 0V40" stroke="#0C4550" strokeWidth="0.5" strokeOpacity="0.3" />
             <path d="M0 40H40" stroke="#0C4550" strokeWidth="0.5" strokeOpacity="0.3" />
             <rect x="18" y="18" width="4" height="4" fill="#0C4550" fillOpacity="0.1" />
          </pattern>
          <linearGradient id="fade-overlay" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="white" stopOpacity="0" />
            <stop offset="100%" stopColor="white" stopOpacity="0.8" />
          </linearGradient>
        </defs>
        <rect width="100%" height="100%" fill="url(#puzzle-grid)" />
        <rect width="100%" height="100%" fill="url(#fade-overlay)" />
      </svg>
    </div>
  );
};

// --- 4. Step Card (Kept Same) ---
const StepCard = ({ number, title, desc, icon, delay, isLast }) => {
  return (
    <div className="relative flex-1 group/card">
      {!isLast && (
        <div className="hidden lg:block absolute top-10 left-[60%] w-[80%] h-[1px] bg-gray-200 z-0 overflow-hidden">
           <motion.div 
            initial={{ x: "-100%" }}
            whileInView={{ x: "100%" }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            className="w-full h-full bg-gradient-to-r from-transparent via-[#F46653] to-transparent opacity-40"
          />
        </div>
      )}

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-50px" }}
        transition={{ duration: 0.6, delay: delay, ease: "easeOut" }}
        className="relative z-10 h-full"
      >
        <div className="h-full bg-white/60 backdrop-blur-md rounded-[2rem] p-8 border border-gray-100 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden relative isolate">
          <PuzzlePattern />
          <span className="absolute -bottom-4 -right-2 text-[100px] font-black text-[#0C4550]/5 leading-none select-none font-serif z-0">
            {number}
          </span>

          <div className="relative z-10 flex flex-col items-center text-center">
            <div className="mb-6">
              <FloatingIcon icon={icon} />
            </div>

            <h3 className="text-xl md:text-2xl font-bold text-[#0C4550] mb-3 group-hover/card:text-[#F46653] transition-colors duration-300">
              {title}
            </h3>
            
            <p className="text-gray-600 font-medium leading-relaxed text-sm md:text-base">
              {desc}
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// --- Main Section ---

const LaunchCampaign = () => {
  return (
    <section className="relative pt-10 pb-24 lg:pt-16 lg:pb-32 overflow-hidden bg-[#fafafa]">
      
      <BackgroundElements />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        
        {/* --- Header Section --- */}
        <div className="text-center mb-16">
          
          {/* Top Tag */}
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/90 border border-gray-200 shadow-sm text-xs font-bold tracking-widest uppercase text-gray-500 mb-6"
          >
            <Zap size={12} className="text-yellow-500 fill-yellow-500" />
            Streamlined Workflow
          </motion.div>

          {/* Heading */}
          <div className="relative">
            <motion.h2 
              className="text-3xl md:text-5xl font-bold text-[#0C4550] tracking-tight mb-2"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              Launch Your
            </motion.h2>
            
            <div className="relative py-2">
              {/* --- CHANGE 1: Static Text (Animation Removed) --- */}
              <h1 className="text-[13vw] md:text-[8rem] leading-[0.9] font-black text-transparent bg-clip-text bg-gradient-to-b from-[#0C4550] to-[#1a6b7a] tracking-tighter drop-shadow-sm select-none">
                CAMPAIGN
              </h1>
              
              <motion.div 
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5, type: "spring" }}
                className="absolute -top-2 right-[10%] md:right-[20%] text-[#F46653]"
              >
                <Sparkles size={40} fill="currentColor" />
              </motion.div>
            </div>

            {/* --- CHANGE 2: Premium Professional Animation for "3 Easy Steps" --- */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8, y: 20 }}
              whileInView={{ opacity: 1, scale: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ 
                type: "spring", 
                stiffness: 200, 
                damping: 20, 
                delay: 0.2 
              }}
              className="relative inline-block mt-8 group"
            >
                {/* Outer Glow Effect */}
                <div className="absolute -inset-1 bg-gradient-to-r from-[#F46653] to-orange-400 rounded-full blur opacity-25 group-hover:opacity-50 transition duration-1000 group-hover:duration-200" />
                
                {/* Main Button */}
                <div className="relative bg-[#F46653] text-white px-8 py-3 rounded-full text-xl font-bold shadow-xl overflow-hidden transform transition-transform duration-300 hover:scale-[1.02] active:scale-95 cursor-pointer">
                    
                    {/* Content */}
                    <span className="relative z-10 flex items-center gap-2">
                        in 3 Easy Steps <ArrowDown size={20} className="animate-bounce" />
                    </span>

                    {/* Shimmer/Sheen Animation */}
                    <motion.div 
                        className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-transparent via-white/30 to-transparent skew-x-[-20deg]"
                        initial={{ x: "-150%" }}
                        animate={{ x: "150%" }}
                        transition={{ 
                            repeat: Infinity, 
                            duration: 2, 
                            ease: "easeInOut", 
                            repeatDelay: 3 
                        }}
                    />
                </div>
            </motion.div>

          </div>
        </div>

        {/* --- Steps Grid (Animation Preserved) --- */}
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 px-2 md:px-8 max-w-7xl mx-auto">
          <StepCard 
            number="01"
            title="Create Account"
            desc="Sign up instantly. DLT registration included to get your unique Sender ID approved in record time."
            icon={UserPlus}
            delay={0.2}
            isLast={false}
          />
          
          <StepCard 
            number="02"
            title="Upload Contacts"
            desc="Drag & drop Excel or CSV files. Our AI sanitizer automatically cleans your list of invalid numbers."
            icon={UploadCloud}
            delay={0.3}
            isLast={false}
          />
          
          <StepCard 
            number="03"
            title="Send & Track"
            desc="Schedule campaigns or blast instantly. Watch live analytics as your message reaches the world."
            icon={Send}
            delay={0.4}
            isLast={true}
          />
        </div>

      </div>
    </section>
  );
};

export default LaunchCampaign;